import { supabase } from '../supabase/client';

export type ErrorSource = 'frontend' | 'backend' | 'provisioning' | 'deployment';
export type ErrorSeverity = 'debug' | 'info' | 'warning' | 'error' | 'critical';

export interface ErrorContext {
  // Chat context (when available)
  chatMessages?: Array<{
    role: 'user' | 'assistant';
    content: string;
    timestamp?: string;
  }>;
  // Current agent being viewed/used
  agentId?: string;
  agentName?: string;
  // Deployment context
  deploymentId?: string;
  vpsInstanceId?: string;
  // Component stack (from React ErrorBoundary)
  componentStack?: string;
  // Any additional context
  [key: string]: unknown;
}

interface ErrorLogPayload {
  // Core error info
  error_type?: string;
  error_message: string;
  error_stack?: string;
  context: ErrorContext;
  
  // Source and severity
  source: ErrorSource;
  severity: ErrorSeverity;
  
  // Frontend specific
  url?: string;
  user_agent?: string;
  user_id?: string;
  
  // Agent specific (optional for frontend)
  agent_id?: string;
  
  // Deployment specific
  deployment_id?: string;
  vps_instance_id?: string;
  
  // Metadata
  app_version?: string;
  environment?: string;
  error_hash?: string;
}

// Simple hash function for grouping similar errors
function hashError(message: string, stack?: string): string {
  const str = `${message}:${stack?.split('\n')[0] || ''}`;
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(16);
}

// Global context that can be updated from anywhere in the app
let globalContext: ErrorContext = {};

export function setErrorContext(context: Partial<ErrorContext>) {
  globalContext = { ...globalContext, ...context };
}

export function addChatMessage(role: 'user' | 'assistant', content: string) {
  if (!globalContext.chatMessages) {
    globalContext.chatMessages = [];
  }
  globalContext.chatMessages.push({
    role,
    content,
    timestamp: new Date().toISOString(),
  });
  // Keep only last 20 messages to avoid huge payloads
  if (globalContext.chatMessages.length > 20) {
    globalContext.chatMessages = globalContext.chatMessages.slice(-20);
  }
}

export function clearChatContext() {
  globalContext.chatMessages = [];
}

export function clearErrorContext() {
  globalContext = {};
}

export async function logError(
  error: Error | string,
  options?: {
    source?: ErrorSource;
    severity?: ErrorSeverity;
    agentId?: string;
    deploymentId?: string;
    vpsInstanceId?: string;
    context?: Partial<ErrorContext>;
  }
): Promise<void> {
  try {
    const errorObj = typeof error === 'string' ? new Error(error) : error;
    const {
      source = 'frontend',
      severity = 'error',
      agentId,
      deploymentId,
      vpsInstanceId,
      context: additionalContext,
    } = options || {};
    
    // Get current user if authenticated
    const { data: { user } } = await supabase.auth.getUser();
    
    const payload: ErrorLogPayload = {
      error_type: errorObj.name,
      error_message: errorObj.message || String(error),
      error_stack: errorObj.stack,
      context: {
        ...globalContext,
        ...additionalContext,
      },
      
      source,
      severity,
      
      url: window.location.href,
      user_agent: navigator.userAgent,
      user_id: user?.id,
      
      agent_id: agentId || globalContext.agentId,
      deployment_id: deploymentId || globalContext.deploymentId,
      vps_instance_id: vpsInstanceId || globalContext.vpsInstanceId,
      
      app_version: import.meta.env.VITE_APP_VERSION || 'unknown',
      environment: import.meta.env.MODE || 'production',
      error_hash: hashError(errorObj.message, errorObj.stack),
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error: insertError } = await supabase
      .from('error_logs')
      .insert(payload as any);

    if (insertError) {
      console.error('[ErrorTracking] Failed to log error to Supabase:', insertError);
    }
  } catch (e) {
    // Silently fail - we don't want error tracking to cause more errors
    console.error('[ErrorTracking] Exception while logging error:', e);
  }
}

// Convenience functions for different error types
export const logFrontendError = (error: Error | string, context?: Partial<ErrorContext>) =>
  logError(error, { source: 'frontend', context });

export const logProvisioningError = (error: Error | string, vpsInstanceId?: string, context?: Partial<ErrorContext>) =>
  logError(error, { source: 'provisioning', severity: 'critical', vpsInstanceId, context });

export const logDeploymentError = (error: Error | string, deploymentId?: string, context?: Partial<ErrorContext>) =>
  logError(error, { source: 'deployment', severity: 'error', deploymentId, context });

// Setup global error handlers
export function setupGlobalErrorHandlers() {
  // Handle uncaught errors
  window.onerror = (message, source, lineno, colno, error) => {
    logError(error || String(message), {
      source: 'frontend',
      severity: 'error',
      context: {
        sourceFile: source,
        lineno,
        colno,
      } as ErrorContext,
    });
    return false;
  };

  // Handle unhandled promise rejections
  window.onunhandledrejection = (event) => {
    const error = event.reason instanceof Error 
      ? event.reason 
      : new Error(String(event.reason));
    logError(error, {
      source: 'frontend',
      severity: 'error',
      context: { type: 'unhandledrejection' },
    });
  };
}
